import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import api from "../../api/client";

// PLM's project and stage deadline alerts, shown to admins beside the user menu.
export default function ProjectAlerts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!user?.isAdmin) {
      setAlerts([]);
      return;
    }
    api
      .get("/projects/overview")
      .then((resp) => setAlerts(resp.data?.alerts || []))
      .catch(() => {
        // ignore alert fetch failures
      });
  }, [user?.isAdmin, location.pathname]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (!user?.isAdmin) return null;

  return (
    <div className="ts-alerts-wrap" ref={wrapRef}>
      <button
        type="button"
        ref={buttonRef}
        className="ts-alerts-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Alerts"
      >
        Alerts
        {alerts.length > 0 && <span className="ts-alerts-count">{alerts.length}</span>}
      </button>
      {open && (
        <div className="ts-alerts-dropdown" role="menu">
          <div className="ts-alerts-header">Project Alerts</div>
          <div className="ts-alerts-list">
            {alerts.length === 0 && <div className="ts-alerts-empty">No alerts</div>}
            {alerts.map((alert) => (
              <button
                key={`${alert.id}-${alert.scope || "project"}`}
                type="button"
                className="ts-alerts-item"
                onClick={() => navigate(`/projects/${alert.id}`)}
              >
                <div className="ts-alerts-title">{alert.name}</div>
                <div className="ts-alerts-meta">
                  {alert.scope === "stage" && alert.stageName ? `Stage: ${alert.stageName}  ` : ""}
                  {alert.durationValue} {alert.durationUnit} · {alert.percent}% elapsed
                </div>
                <span className={`ts-alerts-badge ${alert.level === "overdue" ? "overdue" : "warning"}`}>
                  {alert.scope === "stage"
                    ? alert.level === "overdue"
                      ? `Stage Overdue (${alert.percent}%)`
                      : `Stage Warning (${alert.percent}%)`
                    : alert.level === "overdue"
                      ? `Overdue (${alert.percent}%)`
                      : `80% Warning (${alert.percent}%)`}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
